import { fetchAlertSubscription } from "@/api/hooks/alert";
import { type VoidAsyncResult, createResolvedAsyncResult } from "@/cache/AsyncResult";
import type { ScenePublicApi } from "@/layers/Compositor";
import { type CheckedProps, LayerBase, type PropsChecker } from "@/layers/LayerBase";
import { clearLayerLoading, reportLayerLoading } from "@/layers/geojson/layerLoadingStore";
import { safeFmt } from "@/utility/safeFmt";
import Logger from "@mm/logging";
import type { AlertLayer, GuiTimeZone } from "@mm/metx-workbench.meteomatics.com";
import type { FeatureCollection, Geometry } from "geojson";
import type { DateTime } from "luxon";
import type { GeoJSONSource, GeoJSONSourceSpecification } from "mapbox-gl";
import { alertSubscriptionToGeoJson, emptyAlertGeoJson } from "./AlertLayerImplUtils";

const ALERT_COLOR = "#d1495b";
const ALERT_FILL_OPACITY = 0.25;
const logger = Logger.fromFilename(__filename);

export class AlertLayerImpl extends LayerBase<AlertLayer> {
  private readonly mapboxSourceId: string;
  private readonly mapboxLayerIds: string[];
  private subscriptionRequestVersion = 0;
  private isReleased = false;

  constructor(id: number, props: AlertLayer, scene: ScenePublicApi, timezone: GuiTimeZone) {
    super(id, props, scene, timezone);

    const map = scene.getMapboxMap();
    const layerId = this.humanReadableId();
    this.mapboxSourceId = `${layerId}-source`;
    this.mapboxLayerIds = [`${layerId}-fill`, `${layerId}-outline`, `${layerId}-points`];

    const source: GeoJSONSourceSpecification = {
      type: "geojson",
      data: emptyAlertGeoJson,
    };
    map.addSource(this.mapboxSourceId, source);

    map.addLayer({
      id: this.mapboxLayerIds[0],
      type: "fill",
      source: this.mapboxSourceId,
      filter: ["==", ["geometry-type"], "Polygon"],
      layout: { visibility: props.show ? "visible" : "none" },
      paint: {
        "fill-color": ALERT_COLOR,
        "fill-opacity": ALERT_FILL_OPACITY * props.opacity,
      },
    });
    map.addLayer({
      id: this.mapboxLayerIds[1],
      type: "line",
      source: this.mapboxSourceId,
      filter: ["==", ["geometry-type"], "Polygon"],
      layout: { visibility: props.show ? "visible" : "none" },
      paint: {
        "line-color": ALERT_COLOR,
        "line-opacity": props.opacity,
        "line-width": 2,
      },
    });
    map.addLayer({
      id: this.mapboxLayerIds[2],
      type: "circle",
      source: this.mapboxSourceId,
      filter: ["==", ["geometry-type"], "Point"],
      layout: { visibility: props.show ? "visible" : "none" },
      paint: {
        "circle-color": ALERT_COLOR,
        "circle-opacity": props.opacity,
        "circle-radius": 7,
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 2,
      },
    });

    this.loadSubscription(props.custom_options.reference);
  }

  private setGeoJsonData(data: FeatureCollection<Geometry>): void {
    const source = this.scene.getMapboxMap().getSource(this.mapboxSourceId);
    if (source?.type === "geojson") {
      (source as GeoJSONSource).setData(data);
    }
  }

  private loadSubscription(subscriptionId: string): void {
    const requestVersion = ++this.subscriptionRequestVersion;
    if (!subscriptionId) {
      this.setGeoJsonData(emptyAlertGeoJson);
      if (this.props?.id != null) {
        clearLayerLoading(this.props.id);
      }
      return;
    }

    if (this.props?.id != null) {
      reportLayerLoading(this.props.id, true);
    }

    void Promise.resolve()
      .then(() => fetchAlertSubscription(subscriptionId))
      .then((subscription) => {
        if (!this.isReleased && requestVersion === this.subscriptionRequestVersion) {
          this.setGeoJsonData(alertSubscriptionToGeoJson(subscription));
          if (this.props?.id != null) {
            clearLayerLoading(this.props.id);
          }
        }
      })
      .catch((error) => {
        if (!this.isReleased && requestVersion === this.subscriptionRequestVersion) {
          logger.warn("Failed to load Alert Tool subscription locations.", error);
          this.setGeoJsonData(emptyAlertGeoJson);
          if (this.props?.id != null) {
            clearLayerLoading(this.props.id);
          }
        }
      });
  }

  humanReadableId(): string {
    return safeFmt`metx.AlertLayer#${this.uid}`;
  }

  get mapboxIndex(): string {
    return this.mapboxLayerIds[0];
  }

  moveZIndex(beforeLayerId?: string): void {
    const map = this.scene.getMapboxMap();
    for (const layerId of this.mapboxLayerIds) {
      map.moveLayer(layerId, beforeLayerId);
    }
  }

  checker(): PropsChecker<AlertLayer, LayerBase<AlertLayer>> {
    type CheckerProps = CheckedProps<AlertLayer>;
    const checkLayerProp = (property: CheckerProps) => (prev: AlertLayer, curr: AlertLayer) => {
      const changed = prev[property] !== curr[property];
      if (!changed) {
        return false;
      }

      this.setLayerProps({ [property]: curr[property] });
      const map = this.scene.getMapboxMap();
      switch (property) {
        case "show":
          for (const layerId of this.mapboxLayerIds) {
            map.setLayoutProperty(layerId, "visibility", curr.show ? "visible" : "none");
          }
          break;
        case "opacity":
          map.setPaintProperty(this.mapboxLayerIds[0], "fill-opacity", ALERT_FILL_OPACITY * curr.opacity);
          map.setPaintProperty(this.mapboxLayerIds[1], "line-opacity", curr.opacity);
          map.setPaintProperty(this.mapboxLayerIds[2], "circle-opacity", curr.opacity);
          break;
        case "calibrated":
          break;
        case "custom_options":
          this.loadSubscription(curr.custom_options.reference);
          break;
        default: {
          const _exhaustive: never = property;
          return _exhaustive;
        }
      }
      return true;
    };

    return {
      show: checkLayerProp("show"),
      opacity: checkLayerProp("opacity"),
      calibrated: checkLayerProp("calibrated"),
      custom_options: checkLayerProp("custom_options"),
    };
  }

  beforeRender(): void {}

  releaseLayer(): void {
    this.isReleased = true;
  }

  fetchData(_timeFrame: DateTime): VoidAsyncResult {
    return createResolvedAsyncResult(undefined);
  }

  isRebuffering(_time: DateTime): boolean {
    return false;
  }

  getActiveWeatherParametersAsString(): { model: string; parameter: string }[] {
    return [];
  }

  removeLayer(): void {
    this.isReleased = true;
    if (this.props?.id != null) {
      clearLayerLoading(this.props.id);
    }
    const map = this.scene.getMapboxMap();
    for (const layerId of this.mapboxLayerIds) {
      map.removeLayer(layerId);
    }
    map.removeSource(this.mapboxSourceId);
  }
}
